import type { IAppCurrencyConfig, ICurrency } from "../../../store/interfaces/configInterface";
import { AppStrings } from "../../../utils/constants/AppStrings";

export type ClientCurrencyOption = {
  value: string;
  label: string;
};

const CANONICAL_CASH_KEY = "rebet_cash";
const TRIVELTA_CASH_KEY = "trivelta_cash";

/** Compact TCM values that are themselves aggregator map keys (Yoapuesto). */
const COMPACT_WALLET_KEYS = new Set(["MXN"]);

const hasCurrency = (currency: ICurrency | undefined): boolean =>
  Boolean(currency?.shortcode?.trim() || currency?.name?.trim());

const labelFor = (currency: ICurrency | undefined, fallback: string): string =>
  currency?.name?.trim() || currency?.shortcode?.trim() || fallback;

const isCoinKey = (key: string): boolean => {
  const lowered = key.toLowerCase();
  return lowered.endsWith("_coin") || lowered.endsWith("coin");
};

const isTriveltaTenant = (pamAppName: string): boolean => {
  const appName = (pamAppName || "").toLowerCase();
  return /\btrivelta\b/.test(appName) || /\btest03\b/.test(appName);
};

/** Tenant heuristic used only by getFallbackClientCurrencyOptions (tests). */
const cashWalletKey = (pamAppName: string, primaryCurrency: string): string => {
  if (isTriveltaTenant(pamAppName)) return TRIVELTA_CASH_KEY;
  const primary = (primaryCurrency || "").trim().toUpperCase();
  if (COMPACT_WALLET_KEYS.has(primary)) return primary;
  return CANONICAL_CASH_KEY;
};

const coinWalletKey = (cashKey: string): string | null => {
  if (cashKey.endsWith("_cash")) return `${cashKey.slice(0, -5)}_coin`;
  if (cashKey.endsWith("cash")) return `${cashKey.slice(0, -4)}coin`;
  return null;
};

const labelForMapKey = (
  key: string,
  config: IAppCurrencyConfig | null | undefined,
  primaryCurrency: string,
): string => {
  if (isCoinKey(key)) {
    return labelFor(
      config?.secondary_currency,
      AppStrings.SECONDARY_CURRENCY || AppStrings.COIN,
    );
  }
  return labelFor(config?.primary_currency, primaryCurrency || AppStrings.CASH);
};

/**
 * Tenant heuristic when no catalog keys are available. Tests only — the
 * currency field must not use this to rewrite a stored value.
 */
export const getFallbackClientCurrencyOptions = (
  config?: IAppCurrencyConfig | null,
  strings?: {
    pamAppName?: string;
    primaryCurrency?: string;
  },
): ClientCurrencyOption[] => {
  const pamAppName = strings?.pamAppName ?? AppStrings.PAM_APP_NAME;
  const primaryCurrency = strings?.primaryCurrency ?? AppStrings.PRIMARY_CURRENCY;
  const cashKey = cashWalletKey(pamAppName, primaryCurrency);
  const options: ClientCurrencyOption[] = [
    {
      value: cashKey,
      label: labelFor(config?.primary_currency, primaryCurrency || AppStrings.CASH),
    },
  ];

  if (hasCurrency(config?.secondary_currency)) {
    const coinKey = coinWalletKey(cashKey);
    if (coinKey && coinKey !== cashKey) {
      options.push({
        value: coinKey,
        label: labelFor(
          config?.secondary_currency,
          AppStrings.SECONDARY_CURRENCY || AppStrings.COIN,
        ),
      });
    }
  }

  return options;
};

/**
 * Currencies this client can issue free spins in.
 * Requires `currency_code_map` keys from the selected provider. Labels stay
 * display names from app_currency_config. Catalog order is preserved.
 */
export const getClientCurrencyOptions = (
  config?: IAppCurrencyConfig | null,
  strings?: {
    pamAppName?: string;
    primaryCurrency?: string;
    mapKeys?: string[];
  },
): ClientCurrencyOption[] => {
  const primaryCurrency = strings?.primaryCurrency ?? AppStrings.PRIMARY_CURRENCY;
  const mapKeys = (strings?.mapKeys ?? [])
    .map((key) => key.trim())
    .filter(Boolean);

  if (mapKeys.length === 0) return [];

  const labeled = mapKeys.map((key) => ({
    value: key,
    label: labelForMapKey(key, config, primaryCurrency),
  }));
  const labelCounts = new Map<string, number>();
  for (const option of labeled) {
    labelCounts.set(option.label, (labelCounts.get(option.label) ?? 0) + 1);
  }
  return labeled.map((option) =>
    (labelCounts.get(option.label) ?? 0) > 1
      ? { ...option, label: `${option.label} (${option.value})` }
      : option,
  );
};
